import { Clock, ChevronRight, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { useAuth } from '@/hooks/auth/useAuth';
import { UserAvatar } from '@/components/shared/UserAvatar';

const SUPPORT_EMAIL = 'support@runloop.ai';

/**
 * Full-viewport screen rendered when the active organization's subscription
 * window has lapsed (`disabledReason === 'subscription_expired'`). Mirrors
 * {@link OrganizationDisabledScreen}: it replaces the org shell so members
 * can't mount org-scoped pages while the trial is over, but still lets
 * them switch to another organization.
 *
 * - When the billing plugin contributes a cure path (`billingPath`), a
 *   Subscribe button links to it so the org can move to a paid
 *   subscription without leaving the screen to find billing.
 * - Members with another usable org get a one-click switcher. Blocked orgs —
 *   manually disabled ones and other lapsed subscriptions, all of which have
 *   a non-null computed `disabledReason` — are filtered out.
 * - Members whose only org is this one see a contact-support message.
 *
 * The server also rejects every authenticated API call whose active org
 * resolves here with `403 organization_disabled` (carrying
 * `disabledReason`), so this screen is the user-facing half of that gate.
 */
export function TrialExpiredScreen({
  billingPath = null,
}: {
  /** Org-scoped path of the billing cure page, when the plugin is installed. */
  billingPath?: string | null;
} = {}) {
  const { activeMembership, memberships, setActiveOrganizationId, logout } = useAuth();

  const otherUsableOrgs = memberships.filter(
    (m) =>
      m.organization.id !== activeMembership?.organization.id &&
      m.organization.disabledReason == null,
  );

  // A lapsed paid subscription reaches this screen too (a voluntary cancel
  // leaves the window to expire naturally); the copy must not call it a
  // trial.
  const wasPaid = activeMembership?.organization.subscription?.type === 'paid';
  const orgName = activeMembership?.organization.name;
  const title = wasPaid
    ? orgName
      ? `${orgName}'s subscription has ended`
      : 'Your subscription has ended'
    : orgName
      ? `${orgName}'s trial has ended`
      : 'Your trial has ended';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Alert className="max-w-md">
        <Clock className="h-4 w-4" />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="space-y-4">
          {billingPath && (
            <>
              <p>Subscribe to keep using this organization without interruption.</p>
              <Button asChild size="sm" data-testid="trial-expired-subscribe">
                <Link to={billingPath}>
                  <CreditCard className="h-4 w-4" />
                  Subscribe
                </Link>
              </Button>
            </>
          )}
          {otherUsableOrgs.length > 0 ? (
            <>
              <p>
                Switch to one of your other organizations to keep working, or email{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="font-medium underline underline-offset-2"
                >
                  {SUPPORT_EMAIL}
                </a>{' '}
                to continue with this organization.
              </p>
              <div className="flex flex-col gap-2">
                {otherUsableOrgs.map((m) => (
                  <Button
                    key={m.organization.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`Switch to ${m.organization.name}`}
                    data-testid={`org-switch-${m.organization.id}`}
                    className="justify-between"
                    onClick={() => {
                      setActiveOrganizationId(m.organization.id);
                      // Bounce the SPA so every hook re-reads the new
                      // active org and the trial gate falls away.
                      window.location.assign('/');
                    }}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <UserAvatar
                        userName={m.organization.name}
                        avatarUrl={m.organization.avatarUrl ?? undefined}
                        size="sm"
                        className="shrink-0"
                      />
                      <span className="truncate">{m.organization.name}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  </Button>
                ))}
              </div>
            </>
          ) : (
            <p>
              {wasPaid ? 'Your subscription has ended. ' : 'Your trial has ended. '}
              To keep using this organization, please email{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium underline underline-offset-2"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void logout();
            }}
          >
            Sign out
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
